"use client"

import Image from "next/image"
import { HttpTypes } from "@medusajs/types"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { Text, Button } from "@medusajs/ui"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { convertToLocale } from "@/lib/util/money"
import { addToCartEventBus } from "@/lib/data/cart-event-bus"
import { useState } from "react"
import type { SearchResultsBlock } from "../../templates/search-template"

type SearchProductCardProps = {
  product: any // MeiliSearch hit (may include exists_on_trgovina from unified index)
  countryCode: string
  customer?: HttpTypes.StoreCustomer | null
  /** When set (global /search only), click emits `search_results_clicked` with this surface. */
  searchResultsClickSurface?: "search_grid" | "search_list"
  /** /search block the card sits in, sent as `result_block` with the click. */
  searchResultsBlock?: SearchResultsBlock
  /** Eager-load + show immediately for the first above-the-fold cards (LCP). */
  priority?: boolean
}

/** Check if product is a bestseller from MeiliSearch hit metadata (matches getBestsellerProductIds filter logic) */
function isBestseller(product: { metadata?: Record<string, unknown> }): boolean {
  const meta = product?.metadata
  if (!meta) return false
  const bs = meta.bestseller
  const bss = meta.bestsellerStringValue
  if (bs && ["yes", "true", "1"].includes(String(bs).toLowerCase())) return true
  if (bss === 1 || bss === "1" || (bss && ["yes", "true"].includes(String(bss).toLowerCase()))) return true
  return false
}

export default function SearchProductCard({
  product,
  countryCode,
  customer,
  searchResultsClickSurface,
  searchResultsBlock,
  priority,
}: SearchProductCardProps) {
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  // Priority cards (first row) start visible so they paint on SSR without
  // waiting for the client onLoad → opacity transition.
  const [imageLoaded, setImageLoaded] = useState(!!priority)
  const [imageError, setImageError] = useState(false)

  const getPrice = () => {
    if (!product.prices || !Array.isArray(product.prices) || product.prices.length === 0) {
      return null
    }
    const validPrices = product.prices.filter((p: number) => p > 0)
    if (validPrices.length === 0) return null
    return { amount: Math.min(...validPrices), currency_code: "EUR" }
  }

  const getDeliveryInfo = () => {
    if (!product.variant_inventory || !Array.isArray(product.variant_inventory)) {
      return { hasStock: false, deliveryMessage: "", quantity: 0 }
    }
    let krekStock = 0
    let otherWarehouseStock = 0
    let shortestDeliveryDays = Infinity

    product.variant_inventory.forEach((variantInv: any) => {
      if (!variantInv.stock_locations?.length) return
      variantInv.stock_locations.forEach((location: any) => {
        const available = location.available_quantity || 0
        if (available > 0) {
          if (location.location_name === "Krek") {
            krekStock += available
          } else {
            otherWarehouseStock += available
            const days = location.location_metadata?.dobavni_rok
              ? parseInt(location.location_metadata.dobavni_rok) : Infinity
            if (days < shortestDeliveryDays) shortestDeliveryDays = days
          }
        }
      })
    })

    if (krekStock > 0) return { hasStock: true, deliveryMessage: "Na voljo takoj", quantity: krekStock, isKrek: true }
    if (otherWarehouseStock > 0 && shortestDeliveryDays !== Infinity) {
      return { hasStock: true, deliveryMessage: `Dostava v ${shortestDeliveryDays} dneh`, quantity: otherWarehouseStock, isKrek: false }
    }
    return { hasStock: false, deliveryMessage: "", quantity: 0, isKrek: false }
  }

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!product.variants?.length || !product.variants[0]?.id) return
    setIsAddingToCart(true)
    try {
      addToCartEventBus.emitCartAdd({
        lineItems: [{
          productVariant: {
            ...product.variants[0],
            product: { id: product.id, title: product.title, handle: product.handle, thumbnail: product.thumbnail, description: product.description },
          },
          quantity: 1,
        }],
        regionId: "",
      })
    } catch (err) {
      console.error("Failed to add to cart:", err)
    } finally {
      setIsAddingToCart(false)
    }
  }

  const price = getPrice()
  const deliveryInfo = getDeliveryInfo()
  const thumbnail = product.thumbnail || product.images?.[0]?.url
  const isLimitedStock = deliveryInfo.hasStock && deliveryInfo.quantity > 0 && deliveryInfo.quantity <= 10
  const showBestsellerTag = isBestseller(product)
  const isB2BOnly = product.exists_on_trgovina === false
  const handleResultClick = () => {
    if (!searchResultsClickSurface) {
      return
    }
    captureAnalyticsEvent("search_results_clicked", {
      product_id: product.id,
      product_title: product.title,
      product_handle: product.handle,
      result_surface: searchResultsClickSurface,
      result_block: searchResultsBlock,
      destination: isB2BOnly ? "external_b2b" : "product_page",
    })
  }

  const LinkWrapper = ({ children }: { children: React.ReactNode }) => {
    if (isB2BOnly) {
      return (
        <a
          href={`https://horeca.krek.si/si/products/${product.handle}?ref=trgovina_search`}
          target="_blank"
          rel="noopener noreferrer"
          className="block h-full"
          onClick={handleResultClick}
        >
          {children}
        </a>
      )
    }
    return (
      <LocalizedClientLink
        href={`/products/${product.handle}`}
        className="block h-full"
        onClick={handleResultClick}
      >
        {children}
      </LocalizedClientLink>
    )
  }

  return (
    <LinkWrapper>
      <div className={`group relative h-full overflow-hidden rounded-lg bg-white shadow-sm border ${isB2BOnly ? 'border-amber-300' : 'border-neutral-200'} hover:shadow-lg transition-all duration-200 flex flex-col`}>
        {/* Image - object-contain so full product always visible */}
        <div className="relative aspect-[5/6] overflow-hidden bg-neutral-50 flex-shrink-0">
          {/* B2B-only badge */}
          {isB2BOnly && (
            <div className="absolute top-2 left-2 z-10">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-500 text-white shadow-sm">
                Veleprodaja
              </span>
            </div>
          )}
          {/* Bestseller tag - top-left so it doesn't overlap stock badge */}
          {showBestsellerTag && !isB2BOnly && (
            <div className="absolute top-2 left-2 z-10">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-500 text-white shadow-sm">
                Najbolj priljubljeno
              </span>
            </div>
          )}
          {thumbnail && !imageError ? (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                  <div className="animate-pulse w-6 h-6 border-2 border-neutral-300 border-t-neutral-600 rounded-full" />
                </div>
              )}
              <Image
                src={thumbnail}
                alt={product.title}
                fill
                // Grid is 2 cols (<lg) / 3 cols (≥lg); without this next/image
                // assumed 100vw and served the full 1772px original for a ~665px
                // slot. These sizes let the optimizer pick a right-sized AVIF/WebP.
                sizes="(max-width: 1024px) 50vw, 33vw"
                quality={75}
                className={`object-contain object-center transition-transform duration-300 group-hover:scale-105 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                priority={priority}
                onLoad={() => { setImageLoaded(true); setImageError(false) }}
                onError={() => {
                  setImageError(true)
                  setImageLoaded(false)
                  captureAnalyticsEvent("image_load_failed", {
                    product_id: product.id,
                    product_handle: product.handle,
                    image_url: thumbnail,
                    surface: "search_grid",
                  })
                }}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-100">
              <Text className="text-neutral-400 text-2xl">📦</Text>
            </div>
          )}

          {/* Single unified stock badge - same pill, same size, different colours */}
          {deliveryInfo.hasStock && deliveryInfo.deliveryMessage && (
            <div className="absolute top-2 right-2">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium ${
                isLimitedStock
                  ? "bg-amber-100 text-amber-800"
                  : deliveryInfo.isKrek
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-sky-100 text-sky-800"
              }`}>
                {isLimitedStock ? `Samo ${deliveryInfo.quantity}` : deliveryInfo.deliveryMessage}
              </span>
            </div>
          )}
        </div>

        {/* Bottom strip - below image (not overlay) so tall products are never cut */}
        <div className="flex-shrink-0 p-3 bg-white border-t border-neutral-100 space-y-2">
          <Text className="font-semibold text-sm text-neutral-800 leading-tight" title={product.title}>
            {product.title}
          </Text>
          <div className="flex items-center justify-between gap-2">
            <div>
              {price ? (
                <Text className="text-sm font-semibold text-neutral-800">
                  {convertToLocale({ amount: price.amount, currency_code: price.currency_code })}
                </Text>
              ) : (
                <Text className="text-xs text-neutral-500">Zahtevaj ponudbo</Text>
              )}
            </div>
            {deliveryInfo.hasStock && !isB2BOnly && (
              <Button
                variant="secondary"
                size="small"
                className="flex-shrink-0 h-8 w-8 p-0 rounded-full bg-[#6B8CAE] hover:bg-[#8BACD4] text-white border-0 shadow-sm"
                onClick={handleAddToCart}
                disabled={isAddingToCart}
                isLoading={isAddingToCart}
                title="Dodaj v košarico"
              >
                <span className="sr-only">Dodaj v košarico</span>
                <ShoppingBag className="w-4 h-4" fill="currentColor" />
              </Button>
            )}
          </div>
        </div>

      </div>
    </LinkWrapper>
  )
}
